import { InteractionRequiredAuthError } from '@azure/msal-browser';

/**
 * Fotos de Entra de otros empleados (D-EMP-18, CONTRATO §8). El navegador las pide a Microsoft Graph con el token
 * del usuario; GCvscBE no las ve. Necesitan el permiso delegado `ProfilePhoto.Read.All`, que NO está en `loginRequest`
 * (lo comparten todos los módulos): se pide aparte y en silencio, con `acquireTokenSilent`, desde `useTokenFotos`.
 * Nunca hay popup ni redirección: sin token silencioso, el resultado es «sin foto» y la pantalla pinta iniciales.
 *
 * Las fotos viven solo en memoria (como `Blob`), nunca en el almacenamiento del navegador, en logs ni en la URL de
 * la aplicación. Cada avatar crea su propia URL de objeto y la revoca al desmontarse.
 */
export const SCOPES_FOTOS = ['https://graph.microsoft.com/ProfilePhoto.Read.All'];
// `/users/{email}/photos/{tam}/$value` resuelve el correo como UPN: si el de un empleado no coincide con su UPN, Graph
// da 404 (o no tiene ese tamaño) y salen iniciales (un 404 ya se trata como «sin foto»).
const GRAPH_USUARIOS = 'https://graph.microsoft.com/v1.0/users/';
/** Tamaños que se piden: pequeños, que pesan poco (listado 48x48, ficha 96x96). */
export type TamanoFoto = 48 | 96;
/** Solo estos tipos se pintan: un SVG podría ejecutar scripts si alguien abre la URL de objeto en otra pestaña. */
const TIPOS_IMAGEN = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const FALLO_PASAJERO = 30_000;
const EMAIL_VALIDO = /^[^\s@/?#\\]{1,128}@[^\s@/?#\\]{1,126}$/;

/** Devuelve un token de Graph obtenido en silencio, o `null`. Si no puede, lanza: nunca abre una ventana. */
export type ObtenerToken = () => Promise<string | null>;

export interface OpcionesFotos {
  /** Peticiones a Graph a la vez. */
  concurrencia?: number;
  ttlFoto?: number;
  ttlSinFoto?: number;
  /** Cuánto dura el corte por falta de permiso antes de volver a intentarlo. */
  ttlCorte?: number;
  maxCache?: number;
  ahora?: () => number;
  /** Máximo de una petición a Graph, en ms. */
  tiempoMaximo?: number;
}

interface Entrada { blob: Blob | null; hasta: number }

export function crearServicioFotos(opciones: OpcionesFotos = {}) {
  const {
    concurrencia = 4, ttlFoto = 30 * 60_000, ttlSinFoto = 10 * 60_000, ttlCorte = 10 * 60_000, maxCache = 300,
    ahora = Date.now, tiempoMaximo = 10_000,
  } = opciones;

  const cache = new Map<string, Entrada>();
  const enCurso = new Map<string, { interesados: number; promesa: Promise<Blob | null> }>();
  const oyentes = new Set<() => void>();
  const oyentesReintento = new Set<() => void>();
  let temporizadorReintento: ReturnType<typeof setTimeout> | undefined;
  const cola: Array<() => void> = [];
  let activas = 0;
  let corteHasta = 0;
  let pausaHasta = 0;
  let tokenEnCurso: Promise<string | null> | null = null;

  const corteActivo = () => ahora() < corteHasta;
  const avisar = () => oyentes.forEach((o) => o());
  const cortar = () => { corteHasta = ahora() + ttlCorte; avisar(); };

  const guardar = (clave: string, blob: Blob | null, ttl: number) => {
    cache.delete(clave);
    cache.set(clave, { blob, hasta: ahora() + ttl });
    if (cache.size > maxCache) cache.delete(cache.keys().next().value as string);
  };
  const leer = (clave: string): Entrada | undefined => {
    const e = cache.get(clave);
    if (!e) return undefined;
    cache.delete(clave);
    if (e.hasta <= ahora()) return undefined;
    cache.set(clave, e);
    return e;
  };

  const turno = () => new Promise<void>((resolver) => {
    if (activas < concurrencia) { activas += 1; resolver(); } else cola.push(() => { activas += 1; resolver(); });
  });
  const liberar = () => { activas -= 1; cola.shift()?.(); };

  /** Una sola obtención de token a la vez: los demás esperan la misma. */
  const conseguirToken = (obtener: ObtenerToken) => {
    if (!tokenEnCurso) tokenEnCurso = obtener().finally(() => { tokenEnCurso = null; });
    return tokenEnCurso;
  };

  /** Al acabar la pausa de un 429, las filas que se quedaron en iniciales lo intentan de nuevo (una vez cada una). */
  const programarReintento = (espera: number) => {
    if (temporizadorReintento) clearTimeout(temporizadorReintento);
    temporizadorReintento = setTimeout(() => { temporizadorReintento = undefined; oyentesReintento.forEach((o) => o()); }, espera + 50);
  };

  const pedirAGraph = async (clave: string, email: string, tam: TamanoFoto, obtener: ObtenerToken): Promise<Blob | null> => {
    let token: string | null;
    try {
      token = await conseguirToken(obtener);
    } catch (e) {
      // Falta el permiso o el consentimiento: se detecta una vez y se corta el resto. Otro fallo es pasajero.
      if (e instanceof InteractionRequiredAuthError) cortar();
      return null;
    }
    if (!token) return null;
    const control = new AbortController();
    let temporizador: ReturnType<typeof setTimeout> | undefined;
    // Si vence, se aborta y se descarta: la carrera cubre también un fetch que no hace caso de la señal.
    const vencido = new Promise<never>((_, rechazar) => {
      temporizador = setTimeout(() => { control.abort(); rechazar(new Error('tiempo agotado')); }, tiempoMaximo);
    });
    try {
      const r = await Promise.race([fetch(`${GRAPH_USUARIOS}${encodeURIComponent(email)}/photos/${tam}x${tam}/$value`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
        signal: control.signal,
      }), vencido]);
      if (r.ok) {
        const blob = await Promise.race([r.blob(), vencido]);
        const esImagen = TIPOS_IMAGEN.includes(blob.type.split(';')[0].trim().toLowerCase());
        guardar(clave, esImagen ? blob : null, esImagen ? ttlFoto : ttlSinFoto);
        return esImagen ? blob : null;
      }
      if (r.status === 401 || r.status === 403) { cortar(); return null; }
      if (r.status === 429) {
        const segundos = Number(r.headers.get('Retry-After'));
        const espera = Math.min(Math.max(Number.isFinite(segundos) && segundos > 0 ? segundos : 30, 1), 300) * 1000;
        pausaHasta = ahora() + espera;
        programarReintento(espera);
        return null;
      }
      guardar(clave, null, r.status === 404 ? ttlSinFoto : FALLO_PASAJERO);
    } catch {
      guardar(clave, null, FALLO_PASAJERO);
    } finally {
      clearTimeout(temporizador);
    }
    return null;
  };

  const ejecutar = async (clave: string, email: string, tam: TamanoFoto, obtener: ObtenerToken): Promise<Blob | null> => {
    await turno();
    try {
      // Si nadie sigue esperando (la fila salió de la pantalla), o ya se cortó, no se gasta la petición.
      if ((enCurso.get(clave)?.interesados ?? 0) === 0 || corteActivo() || ahora() < pausaHasta) return null;
      return await pedirAGraph(clave, email, tam, obtener);
    } finally {
      liberar();
      enCurso.delete(clave);
    }
  };

  return {
    /** Foto del empleado en ese tamaño, o `null` (iniciales). Nunca lanza. `senal` aborta la espera en cola. */
    pedir(emailBruto: string, obtener: ObtenerToken, senal?: AbortSignal, tam: TamanoFoto = 48): Promise<Blob | null> {
      const email = (emailBruto ?? '').trim().toLowerCase();
      if (!EMAIL_VALIDO.test(email) || corteActivo()) return Promise.resolve(null);
      const clave = `${tam}|${email}`;
      const hit = leer(clave);
      if (hit) return Promise.resolve(hit.blob);
      let fila = enCurso.get(clave);
      if (!fila) {
        // Un solo viaje por email: los demás avatares con el mismo email esperan la misma promesa.
        fila = { interesados: 0, promesa: Promise.resolve(null) };
        enCurso.set(clave, fila);
        fila.promesa = ejecutar(clave, email, tam, obtener);
      }
      const esta = fila;
      esta.interesados += 1;
      senal?.addEventListener('abort', () => { esta.interesados -= 1; }, { once: true });
      return esta.promesa;
    },
    /** `true` mientras dura el corte por falta de permiso en Entra. */
    sinPermiso: corteActivo,
    suscribir(oyente: () => void) { oyentes.add(oyente); return () => { oyentes.delete(oyente); }; },
    /** Avisa cuando acaba la pausa de un 429; devuelve la baja. */
    alReintentar(oyente: () => void) { oyentesReintento.add(oyente); return () => { oyentesReintento.delete(oyente); }; },
    vaciar() {
      cache.clear(); corteHasta = 0; pausaHasta = 0;
      if (temporizadorReintento) { clearTimeout(temporizadorReintento); temporizadorReintento = undefined; }
    },
  };
}

export const fotosGraph = crearServicioFotos();
